import dayjs from "dayjs";
import { ChevronRight as ChevronRightIcon, Info as InfoIcon, MessagesSquare as MessagesSquareIcon } from "@tamagui/lucide-icons";
import { Badge, Body2, Card, Icon, RowLayout, SkeletonBlock, SkeletonText, Subtitle1, XStack, YStack } from "@medbillai/ui";
import { formatCurrency } from "@medbillai/utils/currency";
import { CaseSummary, type CaseSummaryOutcome } from "./CaseSummary";

// Action required (blocked on the user), unread staff message, in progress, or
// a resolved closed-win.
type CaseCardStatus = "ACTION_REQUIRED" | "NEW_MESSAGE" | "IN_PROGRESS" | "RESOLVED_WIN";

// A demoted closed-win: the row shows a "Resolved" badge with a CaseSummary
// result banner embedded below.
export type CaseCardResolvedWin = {
  outcome: CaseSummaryOutcome;
  savings?: number;
  onReview: () => void;
};

// Minimal case shape the card renders — narrowed so callers and test fixtures
// don't need the full query type. `actionRequired` is computed by the parent.
export type CaseCardCase = {
  id: string;
  title?: string | null;
  lastEventSentAt?: string | null;
  /** Bill amount, when known. */
  amount?: number | null;
  actionRequired: boolean;
  /** Unread staff chat; action required wins when both apply. */
  hasNewMessage?: boolean;
  /** Present on a demoted closed-win; drives the Resolved badge + summary. */
  resolvedWin?: CaseCardResolvedWin;
};
export type CaseCardProps = {
  loading?: false;
  case: CaseCardCase;
  onPress?: () => void;
} | {
  loading: true;
  case?: never;
  onPress?: never;
};

// Warning badge for action required, info for an unread staff message, neutral
// "In progress" otherwise, positive "Resolved" for a demoted win.
function StatusBadge({
  status
}: {
  status: CaseCardStatus;
}) {
  if (status === "RESOLVED_WIN") {
    return <Badge status="positive">Resolved</Badge>;
  }
  if (status === "NEW_MESSAGE") {
    return <Badge status="info" iconLeft={MessagesSquareIcon}>
        New message
      </Badge>;
  }
  return status === "ACTION_REQUIRED" ? <Badge status="warning" iconLeft={InfoIcon} data-sentry-element="Badge" data-sentry-component="StatusBadge" data-sentry-source-file="CaseCard.tsx">
      Action required
    </Badge> : <Badge status="neutral" data-sentry-element="Badge" data-sentry-component="StatusBadge" data-sentry-source-file="CaseCard.tsx">In progress</Badge>;
}
export function CaseCard(props: CaseCardProps) {
  const {
    loading = false,
    case: caseItem,
    onPress
  } = props;
  const win = caseItem?.resolvedWin;
  const status: CaseCardStatus = win ? "RESOLVED_WIN" : caseItem?.actionRequired ? "ACTION_REQUIRED" : caseItem?.hasNewMessage ? "NEW_MESSAGE" : "IN_PROGRESS";
  const date = caseItem?.lastEventSentAt ? dayjs(caseItem.lastEventSentAt).format("ddd, MMM D") : undefined;
  const amount = caseItem?.amount != null ? formatCurrency(caseItem.amount, "USD", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }) : undefined;
  const subtitle = [date, amount].filter(Boolean).join(" • ");

  // A resolved win embeds the result banner below the row (no whole-card press —
  // the Review button owns the interaction); other states tap to open.
  return <Card variant="outlined" size="large" testID={caseItem ? `your-cases.card-${caseItem.id}` : "your-cases.card-skeleton"} onPress={win || loading ? undefined : onPress} aria-busy={loading || undefined} data-sentry-element="Card" data-sentry-component="CaseCard" data-sentry-source-file="CaseCard.tsx">
      <YStack gap={win ? "$sp2" : "$sp0"} data-sentry-element="YStack" data-sentry-source-file="CaseCard.tsx">
        <RowLayout primary={<SkeletonText loading={loading} chars={18}>
              <Subtitle1 color="$textDefault" flexShrink={1} numberOfLines={1}>
                {caseItem?.title ?? ""}
              </Subtitle1>
            </SkeletonText>} secondary={loading ? <SkeletonText loading chars={10}>
                <Body2 color="$textSubtle" />
              </SkeletonText> : subtitle ? <Body2 color="$textSubtle">{subtitle}</Body2> : undefined}
      // Badge + chevron live in the outer trailing slot so they center
      // against the whole content block, not the title row.
      rowTrailing={<XStack ai="center" gap="$sp1.5">
              {loading ? <SkeletonBlock width={110} height="$si3" radius="$brFull" /> : <StatusBadge status={status} />}
              {win ? null :
        // No chevron on a resolved win — the whole card isn't
        // tappable, only its Review button.
        <Icon icon={ChevronRightIcon} size="$iconLarge" color="$iconDefault" />}
            </XStack>} data-sentry-element="RowLayout" data-sentry-source-file="CaseCard.tsx" />
        {win && caseItem ? <CaseSummary testID={`your-cases.summary-${caseItem.id}`} outcome={win.outcome} savings={win.savings} onReview={win.onReview} /> : null}
      </YStack>
    </Card>;
}